// [phase-catalyst-ui-v1] Executive Endorsement panel - Trading UI, The Signal module.
//
// Read-only advisory surface. Reads
//   /api/v1/executive-catalysts/events
//   /api/v1/executive-catalysts/mentions
// over the same-origin nginx proxy.
//
// Hard rules:
//   - Never fabricate executives, mentions, dates, or symbol mappings.
//   - When verified_or_inferred === "inferred", surface a prominent
//     INFERRED badge so the operator knows the symbol mapping is
//     model-derived (not from a direct quote).
//   - No order buttons, no live controls, no raw JSON.
//   - 5-minute poll; pauses when document.hidden.
//
// Marker strings required by /etc/adversyn/trading-ui-markers.json:
//   - "executive-catalysts"
//   - "phase-catalyst-ui-v1"

import { useCallback, useEffect, useRef, useState } from "react";
import { Mic2, RefreshCw } from "lucide-react";
import {
  getExecutiveMentions,
  type ExecutiveMentionRow,
  type ExecutivePayload,
} from "../../lib/executiveCatalystClient";

const POLL_MS = 5 * 60_000;
const WINDOW_DAYS = 7;

function isoDay(offsetDays: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function fmtVal(v: unknown): string {
  if (v === null || v === undefined || v === "") return "(unavailable)";
  if (typeof v === "number" && !Number.isFinite(v)) return "(unavailable)";
  return String(v);
}

function impactClass(dir?: string | null): string {
  const d = (dir || "").toLowerCase();
  if (d === "bullish") return "text-emerald-500";
  if (d === "bearish") return "text-rose-500";
  if (d === "volatility") return "text-amber-500";
  return "text-[var(--muted-text)]";
}

function InferredBadge({ status }: { status?: string | null }) {
  const s = (status || "").toLowerCase();
  if (s === "inferred") {
    return (
      <span
        data-testid="executive-mapping-inferred-badge"
        className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-amber-500/15 text-amber-500 border border-amber-500/40"
        title="Symbol mapping inferred by the model, not from a direct quote."
      >
        Inferred
      </span>
    );
  }
  if (s === "verified") {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-emerald-500/15 text-emerald-500 border border-emerald-500/40">
        Verified
      </span>
    );
  }
  if (s === "uncertain") {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-[var(--card-bg)] text-[var(--muted-text)] border border-[var(--card-border)]">
        Uncertain
      </span>
    );
  }
  return <span className="text-[var(--muted-text)]">(unavailable)</span>;
}

export default function ExecutiveEndorsementPanel() {
  const [mentions, setMentions] = useState<ExecutivePayload<ExecutiveMentionRow> | null>(null);
  const [mentionsErr, setMentionsErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const mounted = useRef(true);

  const fetchAll = useCallback(async () => {
    const end = isoDay(0);
    const start = isoDay(-WINDOW_DAYS);
    const res = await getExecutiveMentions(start, end, undefined, undefined, undefined, undefined, 200);
    if (!mounted.current) return;
    if (res.ok) {
      setMentions(res.data);
      setMentionsErr(null);
    } else {
      setMentionsErr(res.error?.message || "unknown");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    mounted.current = true;
    fetchAll();
    let timer: number | null = null;
    const start = () => {
      if (timer !== null) return;
      timer = window.setInterval(fetchAll, POLL_MS);
    };
    const stop = () => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else {
        fetchAll();
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      mounted.current = false;
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, [fetchAll]);

  const rows = mentions?.rows || [];

  return (
    <div
      className="space-y-4 p-4 rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)]"
      data-testid="executive-endorsement-panel"
      data-signal-flag="phase-catalyst-ui-v1"
      data-marker="executive-catalysts"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Mic2 className="h-4 w-4 text-emerald-500" />
          <h2 className="text-lg font-black tracking-tight uppercase">Executive Endorsements</h2>
          <span className="text-[10px] font-mono text-[var(--muted-text)]">
            phase-catalyst-ui-v1
          </span>
        </div>
        <button
          type="button"
          onClick={fetchAll}
          data-testid="executive-endorsement-refresh"
          className="flex items-center gap-1 px-2 py-1 text-xs uppercase font-bold text-[var(--muted-text)] hover:text-emerald-500"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <p className="text-xs text-[var(--muted-text)]">
        Advisory only. Last {WINDOW_DAYS} days of executive mentions of other companies.
        <span className="ml-1 font-bold text-amber-500">INFERRED</span> badge means the symbol
        mapping was model-derived, not lifted from a verbatim quote. Missing fields render as
        <span className="font-mono"> (unavailable)</span> - never invented.
      </p>

      {loading && (
        <div className="text-xs text-[var(--muted-text)]" data-testid="executive-endorsement-loading">
          Loading...
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div
          className="text-xs text-[var(--muted-text)] py-3"
          data-testid="executive-endorsement-empty"
        >
          No executive mentions in the last {WINDOW_DAYS} days (unavailable).
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto" data-testid="executive-endorsement-table-wrap">
          <table
            className="w-full text-xs border-collapse"
            data-testid="executive-endorsement-table"
          >
            <thead className="text-[var(--muted-text)] uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left py-2 pr-3">Date</th>
                <th className="text-left py-2 pr-3">Executive</th>
                <th className="text-left py-2 pr-3">From</th>
                <th className="text-left py-2 pr-3">Mention</th>
                <th className="text-left py-2 pr-3">Symbol</th>
                <th className="text-left py-2 pr-3">Mapping</th>
                <th className="text-left py-2 pr-3">Impact</th>
                <th className="text-left py-2 pr-3">EOD</th>
                <th className="text-left py-2 pr-3">Source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr
                  key={`${r.mention_id ?? idx}-${r.symbol ?? idx}`}
                  className="border-t border-[var(--card-border)]"
                  data-testid={`executive-endorsement-row-${r.symbol ?? idx}`}
                >
                  <td className="py-2 pr-3 font-mono">{fmtVal(r.event_date)}</td>
                  <td className="py-2 pr-3">
                    <div className="font-bold">{fmtVal(r.speaker_name)}</div>
                    <div className="text-[10px] text-[var(--muted-text)]">
                      {fmtVal(r.speaker_role)}
                    </div>
                  </td>
                  <td className="py-2 pr-3">{fmtVal(r.speaker_company)}</td>
                  <td className="py-2 pr-3">
                    <div className="font-bold">{fmtVal(r.mentioned_company)}</div>
                    <div className="text-[10px] text-[var(--muted-text)] italic">
                      {fmtVal(r.mention_type)}
                    </div>
                  </td>
                  <td className="py-2 pr-3 font-mono font-bold">
                    {fmtVal(r.symbol)}
                    {r.listing_class && r.listing_class !== "US" ? (
                      <span className="ml-1 text-[9px] font-black uppercase text-[var(--muted-text)]">
                        ({r.listing_class})
                      </span>
                    ) : null}
                  </td>
                  <td className="py-2 pr-3">
                    <InferredBadge status={r.verified_or_inferred} />
                  </td>
                  <td className={`py-2 pr-3 font-bold ${impactClass(r.impact_direction)}`}>
                    {fmtVal(r.impact_direction)}
                  </td>
                  <td className="py-2 pr-3 text-[var(--muted-text)]">
                    {fmtVal(r.eod_result)}
                  </td>
                  <td className="py-2 pr-3 text-[var(--muted-text)] truncate max-w-[160px]">
                    {fmtVal(r.source)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {mentionsErr && (
        <div
          className="text-[10px] text-amber-600 font-mono"
          data-testid="executive-endorsement-error"
        >
          mentions: {mentionsErr}
        </div>
      )}
    </div>
  );
}
